import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpMethod, HttpPlus } from 'src/app/SayTS/http-plus';
import { SharedService } from 'src/app/_shared/shared.service';
import { Fce } from 'src/app/SayTS/fce';
import { RepService } from '../../services/rep.service';
import { parseTime, toServerTime } from 'src/app/_shared/date-utils';

@Component({
  selector: 'app-admin-location',
  templateUrl: './admin-location.component.html',
  styleUrls: ['./admin-location.component.scss']
})
export class AdminLocationComponent implements OnInit {
  location: any = null;
  locationSettings: any = {};
  stations: any[] = [];
  isNew = false;
  disabled = false;
  clients: any[] = [];

  readonly STATION_LIMIT = 5;

  constructor(
    private http: HttpPlus,
    private repService: RepService,
    private route: ActivatedRoute,
    private router: Router,
    public shared: SharedService
  ) {}

  async ngOnInit(): Promise<void> {
    this.disabled = !this.shared.rights?.r_edit;
    // Načíst klienty
    await this.loadClients();

    const id = this.route.snapshot.params.id_location;

    // Pokud není id, jedná se o novou lokaci (route /admin/locations/new)
    if (!id) {
      this.isNew = true;
      this.location = {
        name: '',
        id_client: null,
        street: '',
        city: '',
        zip: '',
        is_active: true
      };
      this.locationSettings = {
        is_rep_enabled: true,
        is_open_24_7: true,
        opening_time: null,
        closing_time: null
      };
      // Nová lokace musí mít aspoň jednu stanici
      this.stations = [this.newStation()];
    } else {
      await this.loadLocation(id);
    }
  }

  newStation(): any {
    return { name: '', capacity_kwh: 50, latitude: null, longitude: null, is_active: true };
  }

  toggleOpen24x7(): void {
    if (this.locationSettings.is_open_24_7) {
      this.locationSettings.opening_time = null;
      this.locationSettings.closing_time = null;
    } else {
      this.locationSettings.opening_time = parseTime('06:00');
      this.locationSettings.closing_time = parseTime('22:00');
    }
  }

  addStation(): void {
    if (this.stations.length >= this.STATION_LIMIT) return;
    this.stations.push(this.newStation());
  }

  removeStation(i: number): void {
    if (this.stations.length <= 1) return; // min 1 musí zůstat
    this.stations.splice(i, 1);
  }

  trackByIndex(i: number): number {
    return i;
  }

  async loadClients(): Promise<void> {
    try {
      const response: any = await this.shared.getCached(this.http, 'clients/all', HttpMethod.GET);
      if (response) {
        this.clients = response;
      }
    } catch (error) {
      this.shared.reportError(error, 'Error loading clients');
    }
  }

  async loadLocation(id: number): Promise<void> {
    try {
      const [location, settings] = await Promise.all([
        this.http.get(`locations/get/${id}`) as Promise<any>,
        this.repService.getLocationSettings(id).catch(() => null)
      ]);

      if (location) {
        this.location = location;
        // Stanice přijdou jako relation; pokud lokace zatím nemá žádnou,
        // zobrazíme jednu prázdnou (min 1 required).
        this.stations = (this.location.stations && this.location.stations.length) ? this.location.stations : [this.newStation()];
        // Odstranit referenci v location, aby se neposlala 2× v payloadu
        delete this.location.stations;
      }

      if (settings) {
        this.locationSettings = {
          ...settings,
          opening_time: parseTime(settings.opening_time as any),
          closing_time: parseTime(settings.closing_time as any)
        };
        // Otevřeno 24/7 = nemá nastavené časy
        this.locationSettings.is_open_24_7 = !this.locationSettings.opening_time && !this.locationSettings.closing_time;
      } else {
        this.locationSettings = {
          id_location: id,
          is_rep_enabled: false,
          is_open_24_7: true,
          opening_time: null,
          closing_time: null
        };
      }
    } catch (error) {
      this.shared.reportError(error, 'Error loading location');
      this.shared.error('Nepodařilo se načíst lokaci');
    }
  }

  async save(): Promise<void> {
    if (!this.stations.length) {
      this.shared.warning('Musí být zadána alespoň jedna stanice');
      return;
    }
    if (this.stations.some(s => !s.name)) {
      this.shared.warning('Vyplňte název u všech stanic');
      return;
    }
    try {
      // Uložit lokaci + stanice
      const locationParams = Fce.convertForServer(Fce.copy(this.location, true));
      locationParams.stations = Fce.convertForServer(Fce.copy(this.stations, true));
      const locationResponse: any = await this.http.post('locations/save', locationParams);

      if (locationResponse) {
        const locationId = locationResponse.id_location || this.location.id_location;

        // Uložit REP nastavení (24/7 = oba časy null)
        const open24x7 = !!this.locationSettings.is_open_24_7;
        const settingsParams: any = {
          id_location: locationId,
          is_rep_enabled: this.locationSettings.is_rep_enabled,
          opening_time: open24x7 ? null : toServerTime(this.locationSettings.opening_time),
          closing_time: open24x7 ? null : toServerTime(this.locationSettings.closing_time)
        };

        await this.repService.saveLocationSettings(settingsParams);

        this.shared.success('Lokace uložena');

        if (this.isNew) {
          this.router.navigate(['/admin/locations', locationId], { replaceUrl: true });
        } else {
          this.router.navigate(['/admin/locations']);
        }
      }
    } catch (error) {
      this.shared.reportError(error, 'Error saving location');
      this.shared.error('Nepodařilo se uložit lokaci');
    }
  }

  async delete(): Promise<void> {
    if (await this.shared.confirm('Opravdu smazat tuto lokaci?')) {
      try {
        const response: any = await this.http.delete(`locations/delete/${this.location.id_location}`);
        if (response) {
          this.shared.warning('Lokace smazána');
          this.router.navigate(['/admin/locations']);
        }
      } catch (error) {
        this.shared.reportError(error, 'Error deleting location');
        this.shared.error('Nepodařilo se smazat lokaci');
      }
    }
  }

  async copyApiKey(): Promise<void> {
    const key = this.locationSettings?.api_key;
    if (!key) return;
    try {
      await navigator.clipboard.writeText(key);
      this.shared.success('API klíč zkopírován');
    } catch (error) {
      this.shared.reportError(error, 'Error copying API key');
    }
  }

  async regenerateApiKey(): Promise<void> {
    if (await this.shared.confirm('Opravdu vygenerovat nový API klíč? Stávající klíč přestane fungovat.')) {
      try {
        const result = await this.repService.regenerateApiKey(this.location.id_location);
        if (result) {
          this.locationSettings.api_key = result.api_key;
          this.shared.success('Nový API klíč vygenerován');
        }
      } catch (error) {
        this.shared.reportError(error, 'Error regenerating API key');
        this.shared.error('Nepodařilo se vygenerovat API klíč');
      }
    }
  }

  goBack(): void {
    this.router.navigate(['/admin/locations']);
  }
}
